import { useLocation } from "@remix-run/react";
import { useEffect, useState } from "react";

/**
 * Campaign attribution without touching the visitor's device. utm_* params
 * ride along in the URL (useLocalizedPath re-appends them to internal links)
 * and are submitted with the forms as hidden fields. Nothing is written to
 * cookies or local storage, so §25 TDDDG consent is not in play; storing the
 * values with an enquiry is covered in the privacy policy.
 */
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/** The utm_* portion of a search string ("?utm_source=x"), or "" if none. */
export function utmSearch(search: string): string {
  const current = new URLSearchParams(search);
  const utm = new URLSearchParams();

  for (const key of UTM_KEYS) {
    const value = current.get(key)?.trim();
    if (value) utm.set(key, value.slice(0, 200));
  }

  const encoded = utm.toString();
  return encoded ? `?${encoded}` : "";
}

/** utm_* params of the current URL as entries, for hidden form fields. */
export function useUtmEntries(): [string, string][] {
  const { search } = useLocation();
  return [...new URLSearchParams(utmSearch(search)).entries()];
}

/**
 * The external referrer, if any. Read after hydration: document.referrer does
 * not exist during the server render, so reading it inline would make the
 * server and client markup disagree.
 */
export function useExternalReferrer(): string {
  const [referrer, setReferrer] = useState("");

  useEffect(() => {
    try {
      const value = document.referrer;
      if (value && new URL(value).origin !== window.location.origin) {
        setReferrer(value.slice(0, 400));
      }
    } catch {
      // A malformed referrer is not worth an error; leave it empty.
    }
  }, []);

  return referrer;
}
